<script lang="ts">
  import { onMount } from 'svelte';
  import type { BillingPlan, PaymentHistory } from '../../models/state';
  import { getBillingData, downloadInvoice, cancelSubscription } from '../../utils/billing';

  let billingLoading = $state(false);
  let billingError = $state("");
  let billingSuccess = $state("");

  let currentPlan = $state<BillingPlan | null>(null);
  let paymentHistory = $state<PaymentHistory[]>([]);
  let nextPaymentDate = $state("");

  let currentBillingPage = $state(1);
  let itemsPerBillingPage = 5;

  const totalBillingPages = $derived(
    Math.ceil(paymentHistory.length / itemsPerBillingPage)
  );

  const displayedPaymentHistory = $derived(() => {
    const start = (currentBillingPage - 1) * itemsPerBillingPage;
    return paymentHistory.slice(start, start + itemsPerBillingPage);
  });

  onMount(async () => {
    await loadBillingData();
  });

  async function loadBillingData() {
    try {
      billingLoading = true;
      billingError = "";

      const data = await getBillingData();
      currentPlan = data.currentPlan;
      nextPaymentDate = data.nextPaymentDate;
      paymentHistory = data.paymentHistory;
    } catch (e: any) {
      billingError = e.message || "Error loading billing data";
    } finally {
      billingLoading = false;
    }
  }

  function goToBillingPage(page: number) {
    if (page >= 1 && page <= totalBillingPages) {
      currentBillingPage = page;
    }
  }

  async function handleDownloadInvoice(invoiceId: string) {
    try {
      await downloadInvoice(invoiceId);
      billingSuccess = "Invoice download started...";
      setTimeout(() => (billingSuccess = ""), 3000);
    } catch (e: any) {
      billingError = e.message || "Failed to download invoice";
      setTimeout(() => (billingError = ""), 5000);
    }
  }

  function handleUpgradePlan() {
    window.location.hash = "/billing";
  }

  async function handleCancelPlan() {
    if (!confirm("Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.")) {
      return;
    }

    try {
      billingLoading = true;
      await cancelSubscription(false); // Cancel at period end
      billingSuccess = "Subscription will be cancelled at the end of your billing period";
      await loadBillingData(); // Refresh data
      setTimeout(() => (billingSuccess = ""), 5000);
    } catch (e: any) {
      billingError = e.message || "Failed to cancel subscription";
      setTimeout(() => (billingError = ""), 5000);
    } finally {
      billingLoading = false;
    }
  }
</script>

{#if billingLoading}
  <div class="text-center py-12">
    <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mb-4"></div>
    <p class="text-gray-600 dark:text-gray-400">Loading billing information...</p>
  </div>
{:else}
  {#if billingError}
    <div class="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <p class="text-sm text-red-600 dark:text-red-400">{billingError}</p>
    </div>
  {/if}

  {#if billingSuccess}
    <div class="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <p class="text-sm text-green-600 dark:text-green-400">{billingSuccess}</p>
    </div>
  {/if}

  <!-- Current Plan -->
  <section class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mb-8">
    <h2 class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      Current Plan
    </h2>
    <div class="p-6">
      {#if currentPlan}
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-3 bg-brand-100 dark:bg-brand-900/30 rounded-lg">
                <svg class="w-6 h-6 text-brand-500 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{currentPlan.name}</h3>
                <p class="text-3xl font-bold text-brand-500 dark:text-brand-400 mt-1">
                  ${currentPlan.price.toFixed(2)}
                  <span class="text-base font-normal text-gray-600 dark:text-gray-400">/{currentPlan.interval}</span>
                </p>
              </div>
            </div>

            <div class="space-y-2 mb-4">
              {#each currentPlan.features as feature}
                <div class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <svg class="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span>{feature}</span>
                </div>
              {/each}
            </div>

            {#if nextPaymentDate}
              <div class="p-4 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg">
                <div class="flex items-center gap-2 text-sm">
                  <svg class="w-5 h-5 text-brand-500 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span class="font-medium text-gray-900 dark:text-gray-100">Next payment due:</span>
                  <span class="text-gray-700 dark:text-gray-300">{nextPaymentDate}</span>
                </div>
              </div>
            {/if}
          </div>

          <div class="flex flex-col gap-3">
            <button
              type="button"
              onclick={handleUpgradePlan}
              class="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-colors"
            >
              Upgrade Plan
            </button>
            <button
              type="button"
              onclick={handleUpgradePlan}
              class="px-6 py-2.5 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Manage Plan
            </button>
            <button
              type="button"
              onclick={handleCancelPlan}
              class="px-6 py-2.5 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg font-medium transition-colors"
            >
              Cancel Plan
            </button>
          </div>
        </div>
      {:else}
        <div class="text-center py-8">
          <p class="text-gray-600 dark:text-gray-400 mb-4">No active plan</p>
          <button
            onclick={handleUpgradePlan}
            class="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-colors"
          >
            View Plans
          </button>
        </div>
      {/if}
    </div>
  </section>

  <!-- Payment History -->
  <section class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
    <h2 class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      Payment History
    </h2>
    <div class="p-6">
      {#if paymentHistory.length === 0}
        <div class="text-center py-12">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <p class="text-gray-600 dark:text-gray-400">No payment history</p>
        </div>
      {:else}
        <!-- Payment History Table -->
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700">
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Invoice</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Description</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Amount</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Payment Method</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th class="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each displayedPaymentHistory() as payment}
                <tr class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="py-3 px-4 text-sm font-mono text-gray-900 dark:text-gray-100">{payment.invoiceNumber}</td>
                  <td class="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{payment.description}</td>
                  <td class="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{payment.date}</td>
                  <td class="py-3 px-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    ${payment.amount.toFixed(2)} {payment.currency}
                  </td>
                  <td class="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{payment.paymentMethod}</td>
                  <td class="py-3 px-4">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {payment.status === 'paid'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800'
                      : payment.status === 'pending'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'}">
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </td>
                  <td class="py-3 px-4 text-right">
                    <button
                      type="button"
                      onclick={() => handleDownloadInvoice(payment.id)}
                      class="text-sm text-brand-500 dark:text-brand-400 hover:underline font-medium"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        {#if totalBillingPages > 1}
          <div class="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="text-sm text-gray-600 dark:text-gray-400">
              Showing {(currentBillingPage - 1) * itemsPerBillingPage + 1} - {Math.min(currentBillingPage * itemsPerBillingPage, paymentHistory.length)} of {paymentHistory.length} payments
            </div>
            <div class="flex items-center space-x-2">
              <button
                onclick={() => goToBillingPage(currentBillingPage - 1)}
                disabled={currentBillingPage === 1}
                class="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {#each Array.from({ length: totalBillingPages }, (_, i) => i + 1) as page}
                {#if page === 1 || page === totalBillingPages || (page >= currentBillingPage - 1 && page <= currentBillingPage + 1)}
                  <button
                    onclick={() => goToBillingPage(page)}
                    class="px-3 py-2 text-sm border rounded-lg transition-colors {page === currentBillingPage
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}"
                  >
                    {page}
                  </button>
                {:else if (page === currentBillingPage - 2 && currentBillingPage > 3) || (page === currentBillingPage + 2 && currentBillingPage < totalBillingPages - 2)}
                  <span class="px-2 text-gray-500">...</span>
                {/if}
              {/each}

              <button
                onclick={() => goToBillingPage(currentBillingPage + 1)}
                disabled={currentBillingPage === totalBillingPages}
                class="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        {/if}
      {/if}
    </div>
  </section>
{/if}
